import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { useTenantCatalog, date } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';

/** Абоненты тенанта: кого он биллит. */
export function CustomersPage() {
  const { tenantId = '' } = useParams();
  const queryClient = useQueryClient();
  const catalog = useTenantCatalog(tenantId);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ code: '', name: '', email: '', phone: '', taxId: '' });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/tenants/${tenantId}/customers`, {
        code: form.code,
        name: form.name,
        email: form.email || undefined,
        phone: form.phone || undefined,
        taxId: form.taxId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', tenantId] });
      setCreating(false);
      setForm({ code: '', name: '', email: '', phone: '', taxId: '' });
    },
    onError: (err) => setError((err as ApiError).message),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Абоненты</h1>
          <div className="muted small">
            {catalog.isLoading ? ' ' : `Всего: ${catalog.customers.length}`}
          </div>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => { setError(null); setCreating(true); }}>
          <Icon name="plus" />
          Добавить
        </button>
      </div>

      {catalog.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : catalog.customers.length === 0 ? (
        <div className="card">
          <div className="state-title">Абонентов пока нет</div>
          <p className="muted">
            Абонент — тот, кому выставляются счета. Его код указывают в событиях потребления, так что
            заводится он первым.
          </p>
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" />
              Добавить абонента
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Код</th>
                <th>Название</th>
                <th>Контакты</th>
                <th>ИНН</th>
                <th>Статус</th>
                <th>Заведён</th>
              </tr>
            </thead>
            <tbody>
              {catalog.customers.map((customer) => (
                <tr key={customer.id}>
                  <td>
                    <code>{customer.code}</code>
                  </td>
                  <td>{customer.name}</td>
                  <td className="muted small">
                    {[customer.email, customer.phone].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="muted small">{customer.taxId ?? '—'}</td>
                  <td>
                    {customer.status === 'ACTIVE' ? (
                      <span className="badge badge-green">активен</span>
                    ) : (
                      <span className="badge badge-gray">в архиве</span>
                    )}
                  </td>
                  <td className="muted small">{date(customer.created)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <Modal title="Новый абонент" onClose={() => setCreating(false)}>
          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="customer-code">
                Код
              </label>
              <input
                id="customer-code"
                className="input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                autoFocus
              />
              <div className="form-hint">С этим кодом придут события потребления.</div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="customer-name">
                Название
              </label>
              <input
                id="customer-name"
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="customer-email">
                Почта
              </label>
              <input
                id="customer-email"
                className="input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="customer-phone">
                Телефон
              </label>
              <input
                id="customer-phone"
                className="input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="customer-inn">
              ИНН
            </label>
            <input
              id="customer-inn"
              className="input"
              value={form.taxId}
              onChange={(e) => setForm({ ...form, taxId: e.target.value })}
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setCreating(false)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!form.code || !form.name || create.isPending}
              onClick={() => { setError(null); create.mutate(); }}
            >
              Добавить
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
